import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bug, AlertTriangle, CheckCircle, Clock, Search, Plus, X,
  ChevronDown, ChevronUp, ThumbsUp, Send, AlertCircle, Wrench,
  DollarSign, Heart
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { color: 'text-red-400', bg: 'bg-red-500/20', icon: AlertCircle, label: 'Open' },
  investigating: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: Search, label: 'Investigating' },
  in_progress: { color: 'text-blue-400', bg: 'bg-blue-500/20', icon: Wrench, label: 'In Progress' },
  fixed: { color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle, label: 'Fixed' },
  wont_fix: { color: 'text-gray-400', bg: 'bg-gray-500/20', icon: X, label: "Won't Fix" },
  duplicate: { color: 'text-gray-400', bg: 'bg-gray-500/20', icon: X, label: 'Duplicate' }
};

const SEVERITY_CONFIG = {
  critical: { color: 'text-red-500', label: '🔴 Critical' },
  high: { color: 'text-orange-400', label: '🟠 High' },
  medium: { color: 'text-yellow-400', label: '🟡 Medium' },
  low: { color: 'text-green-400', label: '🟢 Low' }
};

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'gameplay', label: 'Gameplay' },
  { value: 'ui', label: 'UI/UX' },
  { value: 'payment', label: 'Payment' },
  { value: 'auth', label: 'Authentication' },
  { value: 'other', label: 'Other' }
];

export const BugTrackerWidget = ({ compact = false }) => {
  const { user, token } = useAuth();
  const [bugs, setBugs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    loadBugs();
  }, []);

  const loadBugs = async () => {
    try {
      const response = await axios.get(`${API}/bugs/list?limit=10`);
      setBugs(response.data.bugs || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load bugs:', error);
    } finally {
      setLoading(false);
    }
  };

  const voteBug = async (bugId) => {
    if (!token) {
      toast.error('Please login to vote');
      return;
    }
    try {
      await axios.post(`${API}/bugs/${bugId}/vote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vote recorded!');
      loadBugs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Already voted');
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-4 animate-pulse">
        <div className="h-6 bg-babylon-obsidian/50 rounded w-1/3 mb-4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-babylon-obsidian/50 rounded"></div>
          <div className="h-4 bg-babylon-obsidian/50 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-red-500/10 to-transparent"
        >
          <div className="flex items-center gap-3">
            <Bug className="w-5 h-5 text-red-400" />
            <div className="text-left">
              <h3 className="font-heading text-lg text-babylon-gold">Bug Tracker</h3>
              <p className="text-xs text-babylon-text-dim">
                {stats.open || 0} open • {stats.in_progress || 0} in progress • {stats.fixed || 0} fixed
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-babylon-gold" /> : <ChevronDown className="w-5 h-5 text-babylon-gold" />}
        </button>

        {/* Support Message */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              {/* Support Banner */}
              <div className="px-4 py-3 bg-gradient-to-r from-babylon-gold/10 to-purple-500/10 border-y border-babylon-gold/20">
                <div className="flex items-center gap-2 text-sm">
                  <Heart className="w-4 h-4 text-pink-400 flex-shrink-0" />
                  <p className="text-babylon-text-dim">
                    <span className="text-babylon-gold font-medium">Your support helps fix bugs faster!</span>
                    {' '}Every donation & purchase goes toward development.
                  </p>
                </div>
              </div>

              {/* Bug List */}
              <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                {bugs.length === 0 ? (
                  <div className="text-center py-6 text-babylon-text-dim">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400/50" />
                    <p>No open bugs reported!</p>
                    <p className="text-xs mt-1">Help us find issues by reporting them.</p>
                  </div>
                ) : (
                  bugs.map(bug => {
                    const statusConfig = STATUS_CONFIG[bug.status] || STATUS_CONFIG.open;
                    const severityConfig = SEVERITY_CONFIG[bug.severity] || SEVERITY_CONFIG.medium;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div
                        key={bug.id}
                        onClick={() => setSelectedBug(bug)}
                        className="p-3 bg-babylon-obsidian/50 rounded-lg border border-babylon-gold/10 hover:border-babylon-gold/30 cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusConfig.bg} ${statusConfig.color}`}>
                                <StatusIcon className="w-3 h-3 inline mr-0.5" />
                                {statusConfig.label}
                              </span>
                              <span className={`text-[10px] ${severityConfig.color}`}>
                                {severityConfig.label}
                              </span>
                              <span className="text-[10px] text-babylon-text-dim">
                                #{bug.id}
                              </span>
                            </div>
                            <p className="text-sm text-babylon-text mt-1 truncate">{bug.title}</p>
                            <p className="text-xs text-babylon-text-dim mt-0.5">
                              {bug.category} • Reported by {bug.reporter_name}
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); voteBug(bug.id); }}
                            className="flex items-center gap-1 px-2 py-1 rounded bg-babylon-obsidian hover:bg-babylon-gold/20 transition-colors"
                            title="Vote for this bug to increase priority"
                          >
                            <ThumbsUp className="w-3 h-3 text-babylon-gold" />
                            <span className="text-xs text-babylon-gold">{bug.votes || 0}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Report Bug Button */}
              <div className="p-4 pt-2 border-t border-babylon-gold/10">
                <Button
                  onClick={() => user ? setShowReportModal(true) : toast.error('Please login to report a bug')}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Report a Bug
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Bug Detail Modal */}
      <AnimatePresence>
        {selectedBug && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedBug(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-babylon-gold/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${STATUS_CONFIG[selectedBug.status]?.bg} ${STATUS_CONFIG[selectedBug.status]?.color}`}>
                      {STATUS_CONFIG[selectedBug.status]?.label}
                    </span>
                    <span className="text-xs text-babylon-text-dim">#{selectedBug.id}</span>
                  </div>
                  <button onClick={() => setSelectedBug(null)} className="text-babylon-text-dim hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <h3 className="font-heading text-lg text-babylon-gold mt-2">{selectedBug.title}</h3>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-babylon-text-dim mb-1">Description</p>
                  <p className="text-sm text-babylon-text">{selectedBug.description}</p>
                </div>
                
                {selectedBug.steps_to_reproduce && (
                  <div>
                    <p className="text-xs text-babylon-text-dim mb-1">Steps to Reproduce</p>
                    <p className="text-sm text-babylon-text whitespace-pre-line">{selectedBug.steps_to_reproduce}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-babylon-text-dim">Category</p>
                    <p className="text-babylon-text capitalize">{selectedBug.category}</p>
                  </div>
                  <div>
                    <p className="text-xs text-babylon-text-dim">Severity</p>
                    <p className={SEVERITY_CONFIG[selectedBug.severity]?.color}>
                      {SEVERITY_CONFIG[selectedBug.severity]?.label}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-babylon-text-dim">Reported by</p>
                    <p className="text-babylon-text">{selectedBug.reporter_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-babylon-text-dim">Votes</p>
                    <p className="text-babylon-gold">{selectedBug.votes || 0} votes</p>
                  </div>
                  <div>
                    <p className="text-xs text-babylon-text-dim">Created</p>
                    <p className="text-babylon-text">{selectedBug.created_at ? new Date(selectedBug.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-babylon-text-dim">Last Updated</p>
                    <p className="text-babylon-text">{selectedBug.updated_at ? new Date(selectedBug.updated_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
                
                {selectedBug.estimated_fix && (
                  <div className="p-3 bg-babylon-gold/10 rounded-lg">
                    <p className="text-xs text-babylon-text-dim">Estimated Fix</p>
                    <p className="text-sm text-babylon-gold">{selectedBug.estimated_fix}</p>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-babylon-gold/20">
                <Button
                  onClick={() => { voteBug(selectedBug.id); }}
                  className="w-full bg-babylon-gold/20 hover:bg-babylon-gold/30 text-babylon-gold"
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Vote for this bug ({selectedBug.votes || 0})
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Bug Modal */}
      <BugReportModal 
        isOpen={showReportModal} 
        onClose={() => setShowReportModal(false)} 
        onSubmit={() => { loadBugs(); setShowReportModal(false); }}
        token={token}
      />
    </>
  );
};

const BugReportModal = ({ isOpen, onClose, onSubmit, token }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    severity: 'medium',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bugs/report`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setFormData({
        title: '',
        description: '',
        category: 'general',
        severity: 'medium',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: ''
      });
      onSubmit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-babylon-gold/20 flex items-center justify-between">
          <h3 className="font-heading text-lg text-babylon-gold flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-400" />
            Report a Bug
          </h3>
          <button onClick={onClose} className="text-babylon-text-dim hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-xs text-babylon-text-dim block mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-2 bg-babylon-obsidian border border-babylon-gold/20 rounded text-babylon-text text-sm focus:outline-none focus:border-babylon-gold/50"
              placeholder="Brief description of the bug"
              required
            />
          </div>
          
          <div>
            <label className="text-xs text-babylon-text-dim block mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full p-2 bg-babylon-obsidian border border-babylon-gold/20 rounded text-babylon-text text-sm focus:outline-none focus:border-babylon-gold/50 min-h-[80px]"
              placeholder="Detailed description of what went wrong"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-babylon-text-dim block mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-2 bg-babylon-obsidian border border-babylon-gold/20 rounded text-babylon-text text-sm focus:outline-none focus:border-babylon-gold/50"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-babylon-text-dim block mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({...formData, severity: e.target.value})}
                className="w-full p-2 bg-babylon-obsidian border border-babylon-gold/20 rounded text-babylon-text text-sm focus:outline-none focus:border-babylon-gold/50"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-xs text-babylon-text-dim block mb-1">Steps to Reproduce (optional)</label>
            <textarea
              value={formData.steps_to_reproduce}
              onChange={(e) => setFormData({...formData, steps_to_reproduce: e.target.value})}
              className="w-full p-2 bg-babylon-obsidian border border-babylon-gold/20 rounded text-babylon-text text-sm focus:outline-none focus:border-babylon-gold/50 min-h-[60px]"
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
            />
          </div>
          
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400"
          >
            {submitting ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Bug Report
              </>
            )}
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default BugTrackerWidget;
